import { useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { generateShareImage } from '../utils/generateShareImage'
import {
  TYPE_INTERPRETATIONS,
  TYPE_COLORS,
  getShareCardContent,
  type TestResult,
  type TypeCode,
} from '@mindtype/shared'

type CardStyle = 'simple' | 'standard' | 'detailed'

const CARD_STYLES: { key: CardStyle; label: string }[] = [
  { key: 'simple', label: '简洁' },
  { key: 'standard', label: '标准' },
  { key: 'detailed', label: '详细' },
]

interface ShareCardProps {
  result: TestResult
  onClose: () => void
}

export function ShareCard({ result, onClose }: ShareCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)
  const [cardStyle, setCardStyle] = useState<CardStyle>('standard')

  const typeCode = result.modes.natural as TypeCode
  const typeInfo = TYPE_INTERPRETATIONS[typeCode]
  const colorConfig = TYPE_COLORS[typeCode]
  const shareContent = getShareCardContent(typeCode)

  const bestMatchColor = TYPE_COLORS[shareContent.bestMatch]
  const oppositeMatchColor = TYPE_COLORS[shareContent.oppositeMatch]

  // 四维度数据 - 从 result.dimensions 获取
  const dimensionLabels = [
    { leftLabel: '内部', rightLabel: '外部' },
    { leftLabel: '系统', rightLabel: '情境' },
    { leftLabel: '内在', rightLabel: '外在' },
    { leftLabel: '闭合', rightLabel: '探索' },
  ]

  const dimensions = result.dimensions.map((dim, index) => {
    const natural = dim.natural
    const total = natural.poleACount + natural.poleBCount
    // 计算 poleB 的百分比（右侧）
    const percentage = total > 0 ? (natural.poleBCount / total) * 100 : 50
    return {
      ...dimensionLabels[index],
      percentage,
    }
  })

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const blob = await generateShareImage(
        {
          typeCode,
          dimensions,
        },
        cardStyle
      )

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `mindtype-${typeCode}-${cardStyle}.png`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to generate image:', error)
    } finally {
      setDownloading(false)
    }
  }

  // 根据风格确定卡片高度比例
  const getAspectRatio = () => {
    switch (cardStyle) {
      case 'simple': return '3 / 3.2'
      case 'standard': return '3 / 4'
      case 'detailed': return '3 / 4.8'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[90vh] flex flex-col">
        {/* 风格选择器 */}
        <div className="border-b border-gray-100 p-3 shrink-0">
          <div className="flex rounded-lg bg-gray-100 p-1">
            {CARD_STYLES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setCardStyle(key)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                  cardStyle === key
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* 预览区域 - 可滚动 */}
        <div className="flex-1 overflow-y-auto p-3">
          <div
            ref={cardRef}
            data-share-card
            className="bg-white relative"
            style={{
              width: '100%',
              aspectRatio: getAspectRatio(),
            }}
          >
            {/* 顶部几何装饰 */}
            <div className="absolute top-0 left-0 right-0 h-12 overflow-hidden">
              <div
                className="absolute -top-6 -right-6 w-20 h-20 rounded-full"
                style={{ background: `linear-gradient(135deg, ${colorConfig.primary}20, ${colorConfig.secondary}20)` }}
              />
              <div
                className="absolute top-2 left-3 w-6 h-6 rounded-full"
                style={{ background: `linear-gradient(135deg, ${colorConfig.primary}10, ${colorConfig.secondary}15)` }}
              />
            </div>

            {/* 主要内容 */}
            <div className="relative z-10 px-4 pt-8 pb-3">
              {/* Logo */}
              <div className="text-center mb-2">
                <h1 className="text-sm font-bold text-gray-800">MindType</h1>
                <p className="text-[10px] text-gray-500">认知风格测评</p>
              </div>

              {/* 类型展示区 */}
              <div
                className="rounded-lg p-3 mb-3"
                style={{ backgroundColor: `${colorConfig.primary}10` }}
              >
                <p className="text-center text-gray-600 text-[10px] mb-1">你的认知类型是</p>

                {/* 类型代码 */}
                <div className="flex justify-center mb-1">
                  <div
                    className="flex items-center justify-center rounded-md px-4 py-1.5 shadow-sm"
                    style={{ backgroundColor: colorConfig.primary }}
                  >
                    <span
                      data-type-badge
                      className="text-xl font-bold tracking-wider"
                      style={{ color: colorConfig.text }}
                    >
                      {typeCode}
                    </span>
                  </div>
                </div>

                {/* 类型名称 */}
                <h2
                  className="text-center text-base font-semibold"
                  style={{ color: colorConfig.primary }}
                >
                  {typeInfo?.name || typeCode}
                </h2>

                {/* 标语 */}
                <p className="text-center text-gray-600 text-[10px] mt-0.5">
                  「{typeInfo?.tagline || ''}」
                </p>
              </div>

              {/* 行为倾向标签 - 标准版和详细版显示 */}
              {(cardStyle === 'standard' || cardStyle === 'detailed') && (
                <div className="mb-3">
                  <div className="space-y-1.5">
                    {shareContent.behaviorTags.map((tag, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: `${colorConfig.primary}08` }}
                      >
                        <div
                          data-trait-dot
                          className="w-1.5 h-1.5 rounded-full shrink-0 translate-y-px"
                          style={{ backgroundColor: colorConfig.primary }}
                        />
                        <span data-trait-text className="text-[11px] text-gray-700 leading-normal">{tag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 四维度图 - 仅详细版显示 */}
              {cardStyle === 'detailed' && (
                <div className="mb-3 p-3 rounded-lg bg-gray-50">
                  <p className="text-[9px] text-gray-500 mb-2 text-center">四维度分析</p>
                  <div className="space-y-2">
                    {dimensions.map((dim, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span data-dimension-label className="text-[9px] text-gray-500 w-6">{dim.leftLabel}</span>
                        <div className="flex-1 h-2 relative flex items-center">
                          {/* 底部线条 */}
                          <div className="absolute inset-x-0 top-1/2 h-0.5 bg-gray-300 -translate-y-1/2" />
                          {/* 指示点 */}
                          <div
                            className="absolute w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm -translate-x-1/2"
                            style={{
                              left: `${dim.percentage}%`,
                              backgroundColor: colorConfig.primary,
                            }}
                          />
                        </div>
                        <span data-dimension-label className="text-[9px] text-gray-500 w-6 text-right">{dim.rightLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 社交配对标签 - 标准版和详细版显示 */}
              {(cardStyle === 'standard' || cardStyle === 'detailed') && (
                <div
                  className="rounded-lg p-3 mb-3"
                  style={{ backgroundColor: '#f9fafb' }}
                >
                  <div className="flex justify-between gap-3">
                    {/* 最佳拍档 */}
                    <div className="flex-1 text-center">
                      <p className="text-[9px] text-gray-500 mb-1">最佳拍档</p>
                      <div
                        data-type-badge
                        className="inline-flex items-center justify-center rounded px-3 py-1 text-[11px] font-bold min-w-[50px]"
                        style={{
                          backgroundColor: bestMatchColor.primary,
                          color: bestMatchColor.text,
                        }}
                      >
                        {shareContent.bestMatch}
                      </div>
                      <p
                        className="text-[9px] mt-0.5"
                        style={{ color: bestMatchColor.primary }}
                      >
                        {TYPE_INTERPRETATIONS[shareContent.bestMatch]?.name}
                      </p>
                    </div>

                    {/* 分隔线 */}
                    <div className="w-px bg-gray-200" />

                    {/* 欢喜冤家 */}
                    <div className="flex-1 text-center">
                      <p className="text-[9px] text-gray-500 mb-1">欢喜冤家</p>
                      <div
                        data-type-badge
                        className="inline-flex items-center justify-center rounded px-3 py-1 text-[11px] font-bold min-w-[50px]"
                        style={{
                          backgroundColor: oppositeMatchColor.primary,
                          color: oppositeMatchColor.text,
                        }}
                      >
                        {shareContent.oppositeMatch}
                      </div>
                      <p
                        className="text-[9px] mt-0.5"
                        style={{ color: oppositeMatchColor.primary }}
                      >
                        {TYPE_INTERPRETATIONS[shareContent.oppositeMatch]?.name}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 二维码 */}
              <div className="flex flex-col items-center pt-1">
                <QRCodeSVG
                  value="https://mindtype.pages.dev"
                  size={48}
                  level="M"
                />
                <p className="text-[9px] text-gray-500 mt-1">测测你的认知风格</p>
              </div>
            </div>

            {/* 底部几何装饰 */}
            <div className="absolute bottom-0 left-0 right-0 h-10 overflow-hidden">
              <div
                className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full"
                style={{ background: `linear-gradient(45deg, ${colorConfig.secondary}15, ${colorConfig.primary}15)` }}
              />
              <div
                className="absolute bottom-2 right-3 w-4 h-4 rounded-full"
                style={{ backgroundColor: `${colorConfig.primary}15` }}
              />
            </div>
          </div>
        </div>

        {/* 操作按钮 - 固定在底部 */}
        <div className="border-t border-gray-100 p-3 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            关闭
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 px-4 py-2 text-white rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 text-sm"
            style={{ backgroundColor: colorConfig.primary }}
          >
            {downloading ? '生成中...' : '保存图片'}
          </button>
        </div>
      </div>
    </div>
  )
}
